import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  UserPlus,
  Eye,
  User,
  Users,
  FileSearch,
  FileText,
  CalendarRange,
  ListChecks,
  Layers,
} from "lucide-react";
import StatusBadge from "../Program/StatusBadge";
import TelaahStaffModal from "./TelaahStaffModal";
import { hitungJumlahHari } from "../../utils/assignmentStorage";

function formatTanggalPendek(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "2-digit" });
  } catch {
    return iso;
  }
}

function Slot({ label, user, color = "slate" }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`h-2 w-2 rounded-full bg-${color}-400`} />
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}:
      </span>
      <span className="truncate text-[12px] font-medium text-slate-800">
        {user?.name || <span className="italic text-slate-400">belum ditugaskan</span>}
      </span>
    </div>
  );
}

function AnggotaSlot({ anggotaIds, userById }) {
  const items = (anggotaIds || []).map((id) => userById[id]).filter(Boolean);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        <Users className="h-3 w-3" /> Anggota:
      </span>
      {items.length === 0 ? (
        <span className="italic text-[12px] text-slate-400">belum ada</span>
      ) : (
        items.map((u) => (
          <span
            key={u.id}
            className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-slate-700"
          >
            <User className="h-3 w-3 text-slate-400" strokeWidth={1.8} />
            {u.name}
          </span>
        ))
      )}
    </div>
  );
}

export default function AssignmentTable({
  programs,
  assignments,
  users,
  canEdit,
  onAssign,
  onOpenTelaah,
  user,
}) {
  const [expanded, setExpanded] = useState(() => new Set());
  const userById = useMemo(() => {
    const m = {};
    if (Array.isArray(users)) {
      users.forEach((u) => {
        if (u && u.id) m[u.id] = u;
      });
    }
    return m;
  }, [users]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div
      data-testid="assignment-table"
      className="overflow-hidden rounded-lg border border-gray-200 bg-white"
    >
      <div className="grid grid-cols-12 gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">
        <div className="col-span-1">Kode</div>
        <div className="col-span-7">Nama Program</div>
        <div className="col-span-2 text-center">Sub-Kegiatan</div>
        <div className="col-span-1 text-center">Ditugaskan</div>
        <div className="col-span-1 text-right">Aksi</div>
      </div>

      {Array.isArray(programs) && programs.map((p) => {
        const isOpen = expanded.has(p.id);
        const assignable = p.subKegiatan.filter(
          (s) => s.specialType !== "AUDIT_INVESTIGASI",
        );
        const assignedCount = assignable.filter((s) => {
          const a = assignments[s.id];
          return a && (a.dalnisId || a.ketuaId || (a.anggotaIds || []).length > 0);
        }).length;

        return (
          <div key={p.id} data-testid={`assign-program-${p.id}`}>
            <div className="grid grid-cols-12 items-center gap-3 border-b border-gray-100 px-4 py-3 hover:bg-gray-50/70">
              <div className="col-span-1">
                <span className="inline-flex items-center rounded bg-[#1e3a5f] px-1.5 py-0.5 font-mono text-[10px] font-bold text-white">
                  {p.kode}
                </span>
              </div>
              <div className="col-span-7 truncate text-[13px] font-semibold text-slate-900" title={p.nama}>
                {p.nama}
              </div>
              <div className="col-span-2 text-center text-[12px] font-semibold text-slate-700">
                {assignable.length}
              </div>
              <div className="col-span-1 text-center text-[12px]">
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                    assignedCount === assignable.length && assignable.length > 0
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : assignedCount === 0
                      ? "border-gray-200 bg-gray-50 text-gray-600"
                      : "border-blue-200 bg-blue-50 text-blue-700"
                  }`}
                >
                  {assignedCount}/{assignable.length}
                </span>
              </div>
              <div className="col-span-1 flex justify-end">
                <button
                  type="button"
                  onClick={() => toggle(p.id)}
                  data-testid={`assign-expand-${p.id}`}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-slate-600 hover:border-[#1e3a5f] hover:text-[#1e3a5f]"
                >
                  {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {isOpen ? (
              <div className="animate-fade-in-up border-b border-gray-200 bg-gray-50/50">
                {p.subKegiatan.map((s) => {
                  const a = assignments[s.id];
                  const isAudit = s.specialType === "AUDIT_INVESTIGASI";
                  return (
                    <div
                      key={s.id}
                      data-testid={`assign-sub-row-${s.id}`}
                      className="grid grid-cols-12 items-start gap-3 border-t border-gray-100 px-4 py-3"
                    >
                      <div className="col-span-12 md:col-span-6">
                        <div className="flex items-start gap-2">
                          {s.kategori ? (
                            <span className="mt-0.5 inline-flex h-5 shrink-0 items-center rounded bg-[#e8edf2] px-1.5 text-[10px] font-bold text-[#1e3a5f]">
                              {s.kategori}
                            </span>
                          ) : null}
                          <div>
                            <div className="text-[12.5px] font-medium leading-snug text-slate-900">
                              {s.nama}
                            </div>
                            <div className="mt-0.5 flex items-center gap-2">
                              <span className="font-mono text-[10.5px] text-slate-500">{s.id}</span>
                              <StatusBadge status={s.status} />
                              {isAudit ? (
                                <span className="inline-flex items-center rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 border border-amber-200">
                                  Audit Investigasi
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-span-12 space-y-1 md:col-span-4">
                        {isAudit ? (
                          <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11.5px] text-amber-900">
                            Penugasan dikelola di modul Audit Investigasi (Phase 6).
                          </div>
                        ) : (
                          <>
                            <Slot label="Dalnis" user={a ? userById[a.dalnisId] : null} color="blue" />
                            <Slot label="Ketua" user={a ? userById[a.ketuaId] : null} color="emerald" />
                            <AnggotaSlot anggotaIds={a?.anggotaIds || []} userById={userById} />
                            {a?.noSt ||
                            a?.untuk ||
                            (a?.tanggalMulai && a?.tanggalSelesai) ||
                            (a?.subPenugasan || []).length > 0 ? (
                              <div className="mt-1 space-y-1 border-t border-gray-100 pt-1.5">
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                  {a?.noSt ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-600">
                                      <FileText className="h-3 w-3 text-slate-400" strokeWidth={1.8} />
                                      {a.noSt}
                                    </span>
                                  ) : null}
                                  {a?.tanggalMulai && a?.tanggalSelesai ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-600">
                                      <CalendarRange className="h-3 w-3 text-slate-400" strokeWidth={1.8} />
                                      {formatTanggalPendek(a.tanggalMulai)} s.d. {formatTanggalPendek(a.tanggalSelesai)}
                                      {(() => {
                                        const jh = a.jumlahHari ?? hitungJumlahHari(a.tanggalMulai, a.tanggalSelesai);
                                        return jh ? ` (${jh} hari)` : "";
                                      })()}
                                    </span>
                                  ) : null}
                                </div>
                                {a?.untuk ? (
                                  <div className="flex items-start gap-1 text-[11px] text-slate-600">
                                    <ListChecks className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" strokeWidth={1.8} />
                                    <span className="line-clamp-2">{a.untuk}</span>
                                  </div>
                                ) : null}
                                {(a?.subPenugasan || []).length > 0 ? (
                                  (() => {
                                    const stCount = a.subPenugasan.filter((sp) => sp.noSt).length;
                                    return (
                                      <div className="flex flex-wrap items-center gap-1">
                                        <span className="inline-flex items-center gap-1 rounded-full border border-[#1e3a5f]/20 bg-[#e8edf2] px-2 py-0.5 text-[10.5px] font-semibold text-[#1e3a5f]">
                                          <Layers className="h-3 w-3" strokeWidth={1.8} />
                                          {a.subPenugasan.length} sub-penugasan
                                          {stCount > 0 ? ` (${stCount} ST)` : ""}
                                        </span>
                                        <span
                                          className="truncate text-[10.5px] text-slate-500"
                                          title={a.subPenugasan.map((sp) => sp.nama).join(", ")}
                                        >
                                          {a.subPenugasan.map((sp) => sp.nama).join(" · ")}
                                        </span>
                                      </div>
                                    );
                                  })()
                                ) : null}
                              </div>
                            ) : null}
                          </>
                        )}
                      </div>

                      <div className="col-span-12 flex justify-end md:col-span-2">
                        {isAudit ? null : (
                          <button
                            type="button"
                            data-testid={`assign-edit-${s.id}`}
                            onClick={() => onAssign(s)}
                            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-semibold ${
                              canEdit
                                ? "border-[#1e3a5f] bg-[#1e3a5f] text-white hover:bg-[#152a46]"
                                : "border-gray-200 bg-white text-slate-700 hover:border-[#1e3a5f] hover:text-[#1e3a5f]"
                            }`}
                          >
                            {canEdit ? (
                              <>
                                <UserPlus className="h-3 w-3" strokeWidth={2.4} />
                                Tugaskan
                              </>
                            ) : (
                              <>
                                <Eye className="h-3 w-3" strokeWidth={2.4} />
                                Lihat
                              </>
                            )}
                          </button>
                        )}
                        
                        {s.id === "P11-03" ? (
                          <button
                            type="button"
                            onClick={() => onOpenTelaah(s)}
                            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-[#1e3a5f] bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white transition-colors"
                          >
                            <FileSearch className="h-3 w-3" />
                            Tool Telaahan
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
      
    </div>
  );
}
